import React from 'react';
import {createRoot, Root} from 'react-dom/client';
import {useTranslation} from 'react-i18next';
import {EButton, EDangerButton, EFlatButton} from 'webapp-lib/Components';

import {Dialog, DialogSizes} from '#/Dialog/Dialog';
import {I18NextNS} from '_/i18n';

type ConfirmationDialogButtonProps = {
  /** text for the cancel button */
  cancelButtonText?: string;
  /** text for the confirmation button */
  confirmationButtonText?: string;
  /** disable the cancel button */
  cancelButtonDisabled?: boolean;
  /** disable the confirmation button */
  confirmationButtonDisabled?: boolean;
  confirmationButtonDisabledTooltip?: string;
  /** turns button into danger button */
  danger?: boolean;
  /** hides the cancel button */
  cancelButtonHidden?: boolean;
};

type ConfirmationDialogProps = {
  headerText?: string;
  message: string | React.ReactNode;
  resolve?: (ok: boolean) => void;
  buttonProps?: ConfirmationDialogButtonProps;
  /** inverts onClose resolve value, used  if normal button ist used as decline button*/
  invertOnCloseResolve?: boolean;
  /** allows to set the width of a dialog in a standardized way*/
  size?: DialogSizes;
};

const ConfirmationDialog = ({
  headerText,
  message,
  resolve,
  buttonProps,
  invertOnCloseResolve,
  size,
}: ConfirmationDialogProps & {
  resolve: (ok: boolean) => void;
}): React.ReactElement => {
  const {t} = useTranslation<I18NextNS>('common');

  const confirmationButtonDisabledTooltip =
    buttonProps?.confirmationButtonDisabled
      ? buttonProps?.confirmationButtonDisabledTooltip
      : undefined;

  return (
    <Dialog
      actions={[
        !buttonProps?.cancelButtonHidden ? (
          <EFlatButton
            key='second'
            autoFocus={buttonProps?.danger}
            disabled={buttonProps?.cancelButtonDisabled}
            testId='cancel'
            onClick={() => resolve?.(false)}
          >
            {buttonProps?.cancelButtonText ?? t('actions.cancel')}
          </EFlatButton>
        ) : (
          <React.Fragment key='none' />
        ),
        buttonProps?.danger ? (
          <EDangerButton
            key='main'
            disabled={buttonProps?.confirmationButtonDisabled}
            testId='confirm'
            title={confirmationButtonDisabledTooltip}
            type='button'
            onClick={() => resolve(true)}
          >
            {buttonProps?.confirmationButtonText ?? t('actions.confirm')}
          </EDangerButton>
        ) : (
          <EButton
            key='main'
            disabled={buttonProps?.confirmationButtonDisabled}
            testId='confirm'
            title={confirmationButtonDisabledTooltip}
            type='button'
            autoFocus
            onClick={() => resolve(true)}
          >
            {buttonProps?.confirmationButtonText ?? t('actions.confirm')}
          </EButton>
        ),
      ]}
      descriptionText={typeof message === 'string' ? message : undefined}
      headerText={headerText}
      size={size}
      testId='ConfirmationDialog'
      isShown
      onClose={() => resolve(invertOnCloseResolve ?? false)}
    >
      {typeof message !== 'string' ? message : undefined}
    </Dialog>
  );
};

let dialogContainerRoot: Root | null = null;

/**
 * Same as showConfirmationDialog,
 * but you only have to specify onConfirmed action.
 * If the user cancels, nothing is done.
 */
export const showSimpleConfirmationDialog = ({
  onConfirmed,
  onDenied,
  ...props
}: Omit<ConfirmationDialogProps, 'resolve'> & {
  onConfirmed: () => void;
  onDenied?: () => void;
}): void => {
  showConfirmationDialog({
    resolve: (ok) => (ok ? onConfirmed() : onDenied?.()),
    ...props,
  });
};

/**
 * showConfirmationDialog renders a dialog with two options similar to window.confirm
 */
export const showConfirmationDialog = ({
  message,
  resolve,
  ...props
}: ConfirmationDialogProps): void => {
  const dialogContainer = document.getElementById('dialog-container');

  if (!dialogContainer) {
    if (typeof message !== 'string') {
      return;
    }
    // eslint-disable-next-line no-alert
    resolve?.(!window.confirm(message));
  } else {
    dialogContainerRoot = dialogContainerRoot ?? createRoot(dialogContainer);

    dialogContainerRoot.render(
      <ConfirmationDialog
        {...props}
        message={message}
        resolve={(result: boolean): void => {
          if (dialogContainerRoot) {
            dialogContainerRoot.unmount();
            dialogContainerRoot = null;
          }
          resolve?.(result);
        }}
      />
    );
  }
};
